import { FC, useEffect, useMemo, useState } from 'react';
import { Form } from '@repo-level/core-front';

import { FormGroup } from 'components/form/groups/FormGroup';
import { Input } from 'components/form/Input';
import { maskCardExpiryDate } from 'helpers/masks';
import {
  cardExpiryDate,
  cardExpiryDateValidator,
  composeValidators,
  required,
} from 'helpers/validators';
import { OfUnion } from 'types/common';
import { useFieldApi, useFieldState } from 'informed';
import { useTranslation } from 'react-i18next';

const validateCardExpiryDate = composeValidators(required, cardExpiryDate);

export const FieldCardExpiryDate: FC<
  OfUnion<Form.MethodField>[Form.MethodFieldType.CardExpiryDate]
> = ({ fieldName }) => {
  const { t } = useTranslation('glossary');
  const [isFocused, setIsFocused] = useState(false);
  const fieldApi = useFieldApi(fieldName);
  const cardExpiryDate = useFieldState(fieldName);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const getPlaceholder = () => {
    if (isFocused || cardExpiryDate?.value) {
      return t('expiryDate');
    }
    return t('expiryDateDefault');
  };

  const error = useMemo(() => {
    if (!cardExpiryDate) return null;
    if (
      typeof cardExpiryDate.value === 'string' &&
      cardExpiryDate.value.length === 5
    ) {
      return cardExpiryDateValidator(cardExpiryDate.value, undefined);
    }
  }, [cardExpiryDate]);

  useEffect(() => {
    if (!error) return fieldApi.setError(undefined);
    fieldApi.setError(error);
  }, [error, fieldApi]);

  return (
    <FormGroup marginBottom={8}>
      <Input
        field={fieldName}
        id={fieldName}
        validate={validateCardExpiryDate}
        animatedPlaceholder={getPlaceholder()}
        onFocus={handleFocus}
        onBlur={handleBlur}
        formatter={maskCardExpiryDate}
        validateOnBlur
      />
    </FormGroup>
  );
};
